import React, { createContext, useState, useContext, useEffect } from "react";
import {
  DbBusiness,
  DbBusinessBookingType,
  DbBusinessModule,
  DbBusinessPaymentType,
  DbBusinessState,
  DbBusinessSubscriptionType,
  DbBusinessVisibility,
  DbWeekDay,
  DbBusinessSetupStep,
} from "canbook-data/business/models";
import { businessService } from "canbook-data/business/service";
import { useRouter } from "next/router";
import { useUser } from "canbook-data/user/user-provider";
import { useBusiness } from "canbook-data/business/provider";
import { Timestamp } from "firebase/firestore/lite";
import { sanitizeString } from "canbook-data/util/string-extensions";
import { config } from "canbook-data/config";

export const ONBOARDING_STEPS: string[] = [
  "Business details",
  "Service type",
  "Set location",
  "Set business hours",
];

export interface BasicBusinessDetail {
  businessName: string;
  description: string;
  website: string;
}

export interface BusinessCategory {
  id: string;
  name: string;
}

export interface BusinessAddress {
  shopNo: string;
  streetName: string;
  area: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  contactCountryCode: string;
  contactNumber: string;
  alternateContactCountryCode: string;
  alternateContact: string;
  latitude: number;
  longitude: number;
}

interface OnboardContext {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  currentStepTitle: string;
  setCurrentStepTitle: (title: string) => void;
  basicBusinessDetail: BasicBusinessDetail;
  setBasicBusinessDetail: (detail: BasicBusinessDetail) => void;
  businessCategory: BusinessCategory;
  setBusinessCategory: (category: BusinessCategory) => void;
  businessAddress: BusinessAddress;
  setBusinessAddress: (address: BusinessAddress) => void;
  businessHours: Map<string, string[]>;
  setBusinessHours: (hours: Map<string, string[]>) => void;
  businessTimezone: string;
  setBusinessTimezone: (timezone: string) => void;
  saveBusiness: () => void;
  savingBusiness: boolean;
  setSavingBusiness: (saving: boolean) => void;
  errorMessage: string;
  setErrorMessage: (message: string) => void;
}

const OnboardContext = createContext<OnboardContext | null>(null);
const SalonCategory = "Salon & Beauty";

export default function OnboardBusinessProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  if (typeof window !== "undefined") {
    localStorage.clear();
  }
  const [currentStep, setCurrentStep] = useState(1);
  const [currentStepTitle, setCurrentStepTitle] = useState(
    ONBOARDING_STEPS[currentStep - 1],
  );
  const [basicBusinessDetail, setBasicBusinessDetail] =
    useState<BasicBusinessDetail>({
      businessName: "",
      description: "",
      website: "",
    });
  const [businessCategory, setBusinessCategory] = useState<BusinessCategory>({
    id: "",
    name: "",
  });
  const [businessAddress, setBusinessAddress] = useState<BusinessAddress>({
    shopNo: "",
    streetName: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    contactCountryCode: "+91",
    contactNumber: "",
    alternateContactCountryCode: "+91",
    alternateContact: "",
    latitude: 0,
    longitude: 0,
  });
  const [businessHours, setBusinessHours] = useState<Map<string, string[]>>(
    new Map([
      ["sun", ["09.00-17.00"]],
      ["mon", ["09.00-17.00"]],
      ["tue", ["09.00-17.00"]],
      ["wed", ["09.00-17.00"]],
      ["thu", ["09.00-17.00"]],
      ["fri", ["09.00-17.00"]],
      ["sat", ["09.00-17.00"]],
    ]),
  );
  const [businessTimezone, setBusinessTimezone] = useState<string>(
    config.TIMEZONE,
  );

  const [savingBusiness, setSavingBusiness] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { user } = useUser();
  const { setSelectedBusiness, setBusinesses, businesses } = useBusiness();
  const router = useRouter();
  const saveBusiness = async () => {
    if (!user) {
      return;
    }

    // Format business URL: lowercase, replace spaces with hyphens, remove special chars
    const sanitizedBusinessName = sanitizeString(
      basicBusinessDetail.businessName,
    );

    // Check if URL already exists
    const existingBusiness = await businessService.getBusinessByUrl(
      sanitizedBusinessName,
    );

    const business_url = existingBusiness
      ? sanitizedBusinessName + Math.floor(Math.random() * 90 + 10)
      : sanitizedBusinessName;

    const digitsOnly = (value?: string) => (value ?? "").replace(/\D/g, "");
    const primaryContact =
      (businessAddress.contactCountryCode || "+91") +
      digitsOnly(businessAddress.contactNumber);
    const alternateContactValue = businessAddress.alternateContact
      ? (businessAddress.alternateContactCountryCode || "+91") +
        digitsOnly(businessAddress.alternateContact)
      : "";

    const business: DbBusiness = {
      business_url,
      address: businessAddress.shopNo,
      locality: businessAddress.streetName,
      area: businessAddress.area,
      city: businessAddress.city,
      state: businessAddress.state,
      country: businessAddress.country,
      pincode: businessAddress.pincode,
      latitude: businessAddress.latitude,
      longitude: businessAddress.longitude,
      timezone: businessTimezone,
      website: basicBusinessDetail.website,
      business_state: DbBusinessState.Created,
      is_deleted: false,
      email: "",
      phone: primaryContact,
      visibility: DbBusinessVisibility.Protected,
      max_booking_window: 30240,
      cancellation_window: null,
      business_hours: Object.fromEntries(
        Array.from(businessHours.entries()).map(([day, hours]) => [
          DbWeekDay[day as keyof typeof DbWeekDay],
          hours,
        ]),
      ),
      id: "",
      name: basicBusinessDetail.businessName,
      category_id: businessCategory.id,
      appointment_phone: alternateContactValue,
      booking_type: DbBusinessBookingType.Confirm,
      payment_type: DbBusinessPaymentType.Cash,
      images: [],
      description2: [
        { title: "About", description: basicBusinessDetail.description },
      ],
      created_by: user.id,
      has_service: false,
      has_waitlist: false,
      modules:
        businessCategory.name === SalonCategory
          ? [DbBusinessModule.Services, DbBusinessModule.Inventory]
          : [DbBusinessModule.Services],
      created_at: Timestamp.now(),
      remaining_setup_steps: [
        DbBusinessSetupStep.AddService,
        DbBusinessSetupStep.AddCalendar,
        DbBusinessSetupStep.AddMembership,
        DbBusinessSetupStep.AddClient,
        DbBusinessSetupStep.SendCampaign,
        DbBusinessSetupStep.MakeFirstBooking,
      ],
      subscription: {
        amount: 0,
        type: DbBusinessSubscriptionType.basic_plan,
        payment_id: "",
        created_at: Timestamp.now(),
        updated_at: Timestamp.now(),
      },
    };

    setSavingBusiness(true);
    businessService
      .saveBusiness(business)
      .then((businessData) => {
        setSavingBusiness(false);
        localStorage.clear();
        setSelectedBusiness(businessData);
        setBusinesses(businesses.concat(businessData));

        if (typeof window !== "undefined" && window.fbq) {
          window.fbq("trackCustom", "BusinessCreated", {
            businessId: businessData.id,
            businessName: businessData.name,
            businessCategory: businessCategory.name,
          });
        }
        router.replace(`/plans`);
      })
      .catch(() => {
        setSavingBusiness(false);
        setErrorMessage("Something went wrong.");
      });
  };

  useEffect(() => {
    const basicBusinessDetail = localStorage.getItem("basicBusinessDetail");
    if (basicBusinessDetail) {
      setBasicBusinessDetail(JSON.parse(basicBusinessDetail));
    }

    const businessCategory = localStorage.getItem("businessCategory");
    if (businessCategory) {
      setBusinessCategory(JSON.parse(businessCategory));
    }

    const businessAddress = localStorage.getItem("businessAddress");
    if (businessAddress) {
      const parsed = JSON.parse(businessAddress);
      setBusinessAddress({
        ...parsed,
        contactCountryCode: parsed.contactCountryCode ?? "+91",
        alternateContactCountryCode:
          parsed.alternateContactCountryCode ?? "+91",
      });
    }

    const businessHours = localStorage.getItem("businessHours");
    if (businessHours) {
      const parsedBusinessHours = new Map<string, string[]>(
        JSON.parse(businessHours),
      );
      setBusinessHours(parsedBusinessHours);
    }
  }, [currentStep]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const storedTimezone = localStorage.getItem("businessTimezone");
    if (storedTimezone) {
      setBusinessTimezone(storedTimezone);
      return;
    }

    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (detectedTimezone) {
      setBusinessTimezone(detectedTimezone);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "basicBusinessDetail",
      JSON.stringify(basicBusinessDetail),
    );

    localStorage.setItem("businessCategory", JSON.stringify(businessCategory));

    localStorage.setItem("businessAddress", JSON.stringify(businessAddress));

    const businessHoursArray = Array.from(businessHours.entries());

    localStorage.setItem("businessHours", JSON.stringify(businessHoursArray));
    localStorage.setItem("businessTimezone", businessTimezone);
  }, [
    basicBusinessDetail,
    businessCategory,
    businessAddress,
    businessHours,
    businessTimezone,
  ]);

  return (
    <OnboardContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        currentStepTitle,
        setCurrentStepTitle,
        basicBusinessDetail,
        setBasicBusinessDetail,
        businessCategory,
        setBusinessCategory,
        businessAddress,
        setBusinessAddress,
        businessHours,
        setBusinessHours,
        businessTimezone,
        setBusinessTimezone,
        saveBusiness,
        savingBusiness,
        setSavingBusiness,
        errorMessage,
        setErrorMessage,
      }}
    >
      {children}
    </OnboardContext.Provider>
  );
}

export const useOnboardBusiness = () => {
  const context = useContext(OnboardContext);
  if (!context) {
    throw new Error(
      "useOnboardBusiness must be used within OnboardBusinessProvider",
    );
  }
  return context;
};
